import { useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { safeLog } from '@/utils/safeLogger';

export function DangerZone() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Nicht authentifiziert');
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-delete-all-data', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      toast.success('Alle Subscriptions und Credits wurden gelöscht', {
        description: `${data.deleted_subscriptions} Subscriptions und ${data.deleted_topups + data.deleted_transactions} Credit-Einträge entfernt`,
      });
      
      setIsDialogOpen(false);
    } catch (error: any) {
      safeLog.error('Error deleting data', error as Error);
      toast.error('Fehler beim Löschen', {
        description: error.message || 'Ein unbekannter Fehler ist aufgetreten',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card className="app-tile border-destructive/50 hover:shadow-[0_0_30px_-5px] hover:shadow-red-500/20 transition-all">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500/10 to-orange-500/10 flex items-center justify-center animate-pulse-glow">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <CardTitle className="text-destructive flex items-center gap-2">
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible Admin-Aktionen. Bitte mit Vorsicht verwenden!
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Diese Aktion löscht ALLE Subscriptions, Credit Top-ups und Credit Transactions aus der Datenbank. 
            Dies kann nicht rückgängig gemacht werden!
          </AlertDescription>
        </Alert>

        <Button
          variant="destructive"
          onClick={() => setIsDialogOpen(true)}
          className="w-full gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Alle Subscriptions & Credits löschen
        </Button>

        <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Bist du absolut sicher?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  Diese Aktion wird ALLE Daten aus folgenden Tabellen PERMANENT löschen:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>subscriptions</li>
                  <li>credit_topups</li>
                  <li>credit_transactions</li>
                </ul>
                <p className="font-semibold text-destructive pt-2">
                  Dies kann nicht rückgängig gemacht werden!
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAll}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Lösche...' : 'Ja, alles löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
